import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../../../config";
import { debounce } from "lodash";
import MainBody from "../../../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../../../components/Pagination";
import sortCaret from "../../../../components/SortCaret";
import AddEditVersion from "./AddEditVersion";
import { ResponsiveDrawerForm } from "../../../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import VersionActionButton from "./VersionActionButton";
import Confirmation from "../../../../components/Confirmation";
import { useOrganization } from "../../../../lib/context/OrganisationContext";

const Versions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [versionsList, setVersionsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { organizationData } = useOrganization();

  const [versionId, setVersionId] = useState(null);
  const [showAddEditVersion, setShowAddEditVersion] = useState(false);
  const [showVersionDelete, setShowVersionDelete] = useState(false);

  const toggleShowAddEditVersion = () => {
    if (showAddEditVersion) {
      setEdit(false);
      setVersionId(null);
    }
    setShowAddEditVersion(!showAddEditVersion);
  };

  const toggleShowDelete = () => {
    if (showVersionDelete) {
      setVersionId(null);
    }
    setShowVersionDelete(!showVersionDelete);
  };

  const fetchVersion = useCallback(
    async (passLimit, passPage) => {
      try {
        setLoading(true);
        const { data } = await get(
          `${API.GET_ADMIN_VERSIONS}?limit=${
            passLimit ? passLimit : limit ? limit : defaultLimitPagination
          }&page=${passPage ? passPage : page ? page : 1}&search=${
            searchParam ? searchParam : ""
          }&sort_column=${sortField ? sortField : ""}&sort_order=${
            sortOrder ? sortOrder : ""
          }`
        );

        setLoading(false);
        setLimit(data?.data?.per_page);
        setPage(data?.data?.current_page);
        setTotal(data?.data?.total);
        setFrom(data?.data?.from);
        setTo(data?.data?.to);
        const versionsList = data.data.data;
        setVersionsList(versionsList);
        return data.data;
      } catch (error) {
        setLoading(false);
        setVersionsList([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField]
  );

  const versionsGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: versionsList[i]?.id,
        id: versionsList[i]?.id,
        version_number: versionsList[i]?.version_number
          ? versionsList[i]?.version_number
          : "",
        name: versionsList[i]?.name ? versionsList[i]?.name : "",
        description: versionsList[i]?.description
          ? versionsList[i]?.description
          : "-",
        is_used: versionsList[i]?.is_used,
        action: (
          <>
            <VersionActionButton
              id={versionsList[i]?.id}
              setEdit={setEdit}
              setVersionId={setVersionId}
              toggleShowAddEditVersion={toggleShowAddEditVersion}
              toggleShowDelete={toggleShowDelete}
              is_used={versionsList[i]?.is_used}
            />
          </>
        ),
      });
    }
    return items;
  };

  const versions = versionsGenerator(versionsList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "version_number",
      text: "Version Number",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?tab=risk-assessment&childTab=versions&limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchVersion();
  }, [fetchVersion]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchVersion(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchVersion(limit, page);
  };

  // Single Version Delete
  const handleVersionDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.ADMIN_VERSION_DELETE}/${versionId}`);
      setLoading(false);
      fetchVersion();
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Add Version */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Version" : "Add Version"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setVersionId(null);
                setShowAddEditVersion(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Version</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditVersion}
              buttonClassName={""}
              setOpen={setShowAddEditVersion}
            >
              <AddEditVersion
                edit={edit}
                toggleShowAddEditVersion={toggleShowAddEditVersion}
                id={versionId}
                orgId={
                  organizationData?.organizationId
                    ? organizationData?.organizationId
                    : ""
                }
                fetchVersionData={() => fetchVersion(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        innerBody={true}
        expandList={true}
        loading={loading}
        className="p-0"
      >
        <div className="custom-table">
          <div className="table-body-class max-h-[550px]">
            <BootstrapTable
              keyField="key"
              remote
              data={versions}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Version"}
        open={showVersionDelete}
        setOpen={setShowVersionDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete version?"}
        okAction={async () => {
          handleVersionDelete();
          toggleShowDelete();
          fetchVersion();
        }}
        cancelAction={() => {
          setVersionId(null);
          setShowVersionDelete(!showVersionDelete);
        }}
      />
    </>
  );
};

export default Versions;
